import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { API_URL } from '../../lib/api-client';
import { shouldUseDemoMode } from '../../utils/demoMode';

interface Activity {
  _id: string;
  userId: string;
  type: string;
  action: string;
  targetId?: string;
  targetModel?: string;
  metadata?: any;
  ipAddress?: string;
  userAgent?: string;
  createdAt: string;
}

interface UserProfile {
  _id: string;
  firstName: string;
  lastName: string;
  age?: number;
  profilePhoto?: string;
}

interface InterestProfile {
  _id: string;
  userId: UserProfile;
  photos: string[];
  age: number;
  city: string;
  state: string;
  education: string | {
    level?: string;
    field?: string;
    institution?: string;
  };
  profession: string;
  income: string;
  height: string;
  religion: string;
  isVerified: boolean;
  isOnline: boolean;
  lastActive: string;
  managedBy?: string;
}

interface Interest {
  _id: string;
  fromUserId: UserProfile;
  toProfile?: InterestProfile;
  toProfileId?: InterestProfile;
  fromUserProfile?: InterestProfile;
  fromProfile?: InterestProfile;
  type: string;
  status: string;
  message?: string;
  createdAt: string;
  metadata?: {
    message?: string;
  };
  fromUser?: UserProfile;
}

interface AnalyticsUser {
  _id: string;
  user: UserProfile;
  profile?: InterestProfile;
  createdAt: string;
}

interface AnalyticsStats {
  profileVisits: number;
  shortlists: number;
  contactViews: number;
}

interface ActivityState {
  activities: Activity[];
  loading: boolean;
  error: string | null;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  activitiesLoaded: boolean;
  // Interests data
  interests: {
    received: Interest[];
    accepted: Interest[];
    sent: Interest[];
    shortlisted: Interest[];
  };
  interestsLoading: boolean;
  interestsError: string | null;
  interestsLoaded: boolean;
  // Analytics data
  analyticsStats: AnalyticsStats;
  analyticsUsers: {
    profile_visit: AnalyticsUser[];
    shortlist: AnalyticsUser[];
    contact_view: AnalyticsUser[];
  };
  analyticsLoading: boolean;
  analyticsError: string | null;
  analyticsStatsLoaded: boolean;
  analyticsUsersLoaded: {
    profile_visit: boolean;
    shortlist: boolean;
    contact_view: boolean;
  };
}

const initialState: ActivityState = {
  activities: [],
  loading: false,
  error: null,
  pagination: {
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  },
  activitiesLoaded: false,
  interests: {
    received: [],
    accepted: [],
    sent: [],
    shortlisted: [],
  },
  interestsLoading: false,
  interestsError: null,
  interestsLoaded: false,
  analyticsStats: {
    profileVisits: 0,
    shortlists: 0,
    contactViews: 0,
  },
  analyticsUsers: {
    profile_visit: [],
    shortlist: [],
    contact_view: [],
  },
  analyticsLoading: false,
  analyticsError: null,
  analyticsStatsLoaded: false,
  analyticsUsersLoaded: {
    profile_visit: false,
    shortlist: false,
    contact_view: false,
  },
};

// Async thunks
export const fetchActivities = createAsyncThunk(
  'activity/fetchActivities',
  async ({ page = 1, limit = 20 }: { page?: number; limit?: number }, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(`${API_URL}/activities/me?page=${page}&limit=${limit}`);
      
      if (data.success) {
        return data.data;
      }
      return rejectWithValue('Failed to fetch activities');
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch activities');
    }
  }
);

export const logActivity = createAsyncThunk(
  'activity/logActivity',
  async (activityData: { type: string; action: string; targetId?: string; targetModel?: string; metadata?: any }, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(`${API_URL}/activities`, activityData);
      
      if (data.success) {
        return data.data;
      }
      return rejectWithValue('Failed to log activity');
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to log activity');
    }
  }
);

// Fetch all interests at once (received, accepted, sent)
export const fetchAllInterests = createAsyncThunk(
  'activity/fetchAllInterests',
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('❌ No token found in fetchAllInterests');
        return rejectWithValue('Not authenticated');
      }
      
      const headers = {
        Authorization: `Bearer ${token}`
      };
      
      const isDev = import.meta.env.DEV;
      
      if (isDev) {
        console.log('🔄 Fetching all interests...');
        console.log('📍 API_URL:', API_URL);
      }
      
      const [receivedRes, acceptedRes, sentRes, shortlistedRes] = await Promise.all([
        axios.get(`${API_URL}/interactions/received?type=interest,superlike&status=pending`, { headers }),
        axios.get(`${API_URL}/interactions/received?type=interest,superlike&status=accepted`, { headers }),
        axios.get(`${API_URL}/interactions/sent?type=interest,superlike`, { headers }),
        axios.get(`${API_URL}/interactions/sent?type=shortlist`, { headers })
      ]);
      
      if (isDev) {
        console.log('📥 Received interests RAW response:', receivedRes?.data);
        console.log('📥 Accepted interests RAW response:', acceptedRes?.data);
        console.log('📥 Sent interests RAW response:', sentRes?.data);
        console.log('📥 Shortlisted RAW response:', shortlistedRes?.data);
      }
      
      const interests = {
        received: receivedRes?.data?.success ? (receivedRes.data.data?.interactions || receivedRes.data.data?.matches || []) : [],
        accepted: acceptedRes?.data?.success ? (acceptedRes.data.data?.interactions || acceptedRes.data.data?.matches || []) : [],
        sent: sentRes?.data?.success ? (sentRes.data.data?.interactions || sentRes.data.data?.matches || []) : [],
        shortlisted: shortlistedRes?.data?.success ? (shortlistedRes.data.data?.interactions || shortlistedRes.data.data?.matches || []) : []
      };
      
      if (isDev) {
        console.log('✅ Parsed interests counts:', {
          received: interests.received.length,
          accepted: interests.accepted.length,
          sent: interests.sent.length,
          shortlisted: interests.shortlisted.length
        });
        console.log('📋 Received interests array:', interests.received);
      }
      
      return interests;
    } catch (error: any) {
      console.error('❌ Error in fetchAllInterests:', error.response?.data || error.message);
      console.error('Full error:', error);
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch interests');
    }
  }
);

// Accept an interest
export const acceptInterest = createAsyncThunk(
  'activity/acceptInterest',
  async ({ interestId, interest }: { interestId: string; interest: Interest }, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(`${API_URL}/interactions/accept/${interestId}`, {});
      
      if (data.success) {
        return { interestId, interest };
      }
      return rejectWithValue('Failed to accept interest');
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to accept interest');
    }
  }
);

// Decline an interest
export const declineInterest = createAsyncThunk(
  'activity/declineInterest',
  async ({ interestId, filterType }: { interestId: string; filterType: 'received' | 'accepted' | 'sent' }, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(`${API_URL}/interactions/decline/${interestId}`, {});
      
      if (data.success) {
        return { interestId, filterType };
      }
      return rejectWithValue('Failed to decline interest');
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to decline interest');
    }
  }
);

// Fetch analytics stats
export const fetchAnalyticsStats = createAsyncThunk(
  'activity/fetchAnalyticsStats',
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const isDemoMode = shouldUseDemoMode() || localStorage.getItem('demoMode') === 'true';
      
      if (!token) {
        return rejectWithValue('Not authenticated');
      }
      
      if (isDemoMode) {
        return {
          profileVisits: 5,
          shortlists: 3,
          contactViews: 2
        };
      }
      
      const { data } = await axios.get(`${API_URL}/profile-analytics/stats`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      if (data.success) {
        return data.data.stats;
      }
      return rejectWithValue('Failed to fetch analytics stats');
    } catch (error: any) {
      if (error.message === 'Network Error' || !error.response) {
        return {
          profileVisits: 0,
          shortlists: 0,
          contactViews: 0
        };
      }
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch analytics stats');
    }
  }
);

// Fetch analytics users list
export const fetchAnalyticsUsers = createAsyncThunk(
  'activity/fetchAnalyticsUsers',
  async (type: 'profile_visit' | 'shortlist' | 'contact_view', { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const isDemoMode = shouldUseDemoMode() || localStorage.getItem('demoMode') === 'true';
      
      if (!token) {
        return rejectWithValue('Not authenticated');
      }
      
      if (isDemoMode) {
        return { type, users: [] };
      }
      
      const { data } = await axios.get(`${API_URL}/profile-analytics/list/${type}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      if (data.success) {
        return { type, users: data.data.list };
      }
      return rejectWithValue('Failed to fetch analytics users');
    } catch (error: any) {
      if (error.message === 'Network Error' || !error.response) {
        return { type, users: [] };
      }
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch analytics users');
    }
  }
);

// Slice
const activitySlice = createSlice({
  name: 'activity',
  initialState,
  reducers: {
    clearActivityError: (state) => {
      state.error = null;
    },
    clearInterestsError: (state) => {
      state.interestsError = null;
    },
    clearAnalyticsError: (state) => {
      state.analyticsError = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch activities
    builder.addCase(fetchActivities.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchActivities.fulfilled, (state, action) => {
      state.loading = false;
      state.activities = action.payload.activities;
      state.pagination = action.payload.pagination;
      state.error = null;
      state.activitiesLoaded = true;
    });
    builder.addCase(fetchActivities.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Log activity
    builder.addCase(logActivity.fulfilled, (state, action) => {
      // Prepend new activity to the list
      state.activities.unshift(action.payload);
    });

    // Fetch all interests
    builder.addCase(fetchAllInterests.pending, (state) => {
      state.interestsLoading = true;
      state.interestsError = null;
    });
    builder.addCase(fetchAllInterests.fulfilled, (state, action) => {
      state.interestsLoading = false;
      state.interests = action.payload;
      state.interestsError = null;
      state.interestsLoaded = true;
    });
    builder.addCase(fetchAllInterests.rejected, (state, action) => {
      state.interestsLoading = false;
      state.interestsError = action.payload as string;
    });

    // Accept interest
    builder.addCase(acceptInterest.fulfilled, (state, action) => {
      const { interestId, interest } = action.payload;
      // Remove from received
      state.interests.received = state.interests.received.filter(i => i._id !== interestId);
      // Add to accepted
      state.interests.accepted.push({ ...interest, status: 'accepted' });
    });

    // Decline interest
    builder.addCase(declineInterest.fulfilled, (state, action) => {
      const { interestId, filterType } = action.payload;
      // Remove from the specified filter type
      state.interests[filterType] = state.interests[filterType].filter(i => i._id !== interestId);
    });

    // Fetch analytics stats
    builder.addCase(fetchAnalyticsStats.pending, (state) => {
      state.analyticsLoading = true;
      state.analyticsError = null;
    });
    builder.addCase(fetchAnalyticsStats.fulfilled, (state, action) => {
      state.analyticsLoading = false;
      state.analyticsStats = action.payload;
      state.analyticsError = null;
      state.analyticsStatsLoaded = true;
    });
    builder.addCase(fetchAnalyticsStats.rejected, (state, action) => {
      state.analyticsLoading = false;
      state.analyticsError = action.payload as string;
    });

    // Fetch analytics users
    builder.addCase(fetchAnalyticsUsers.pending, (state) => {
      state.analyticsLoading = true;
      state.analyticsError = null;
    });
    builder.addCase(fetchAnalyticsUsers.fulfilled, (state, action) => {
      state.analyticsLoading = false;
      const { type, users } = action.payload;
      state.analyticsUsers[type] = users;
      state.analyticsError = null;
      state.analyticsUsersLoaded[type] = true;
    });
    builder.addCase(fetchAnalyticsUsers.rejected, (state, action) => {
      state.analyticsLoading = false;
      state.analyticsError = action.payload as string;
    });
  },
});

export const { clearActivityError, clearInterestsError, clearAnalyticsError } = activitySlice.actions;
export default activitySlice.reducer;